import { registerPlugin } from '@capacitor/core';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ATHENA_EVENT_SOURCES,
  ATHENA_EVENT_TYPES,
  emitAthenaEvent,
} from '../events/AthenaEventBus';
import { useAuth } from '../context/AuthContext';

// Intents are navigation / UI requests only. They intentionally cannot carry
// arbitrary URLs, text, model output or native command parameters.
export const ATHENA_INTENT_TYPES = Object.freeze({
  OPEN_HOME: 'OPEN_HOME',
  OPEN_QUICK: 'OPEN_QUICK',
  OPEN_CHAT: 'OPEN_CHAT',
  OPEN_PROJECT: 'OPEN_PROJECT',
  OPEN_CALENDAR: 'OPEN_CALENDAR',
  OPEN_TASK: 'OPEN_TASK',
  OPEN_WORKSPACE: 'OPEN_WORKSPACE',
  START_VOICE: 'START_VOICE',
  NEW_TASK: 'NEW_TASK',
  NEW_REMINDER: 'NEW_REMINDER',
  CAPTURE_IDEA: 'CAPTURE_IDEA',
  IMPORT_CONTENT: 'IMPORT_CONTENT',
});

const NativeIntents = registerPlugin('AthenaIntents');
const INTENT_TYPES = new Set(Object.values(ATHENA_INTENT_TYPES));
const INTENT_STORAGE_KEY = 'athena_pending_intents_v1';
const INTENT_EVENT_NAME = 'athena-intent-requested';
const MAX_PENDING_INTENTS = 6;
const MAX_INTENT_AGE_MS = 10 * 60 * 1000;
const SAFE_OWNER = /^[A-Za-z0-9._:-]{1,180}$/;

const ACTION_SLUGS = Object.freeze({
  'open-home': ATHENA_INTENT_TYPES.OPEN_HOME,
  'open-quick': ATHENA_INTENT_TYPES.OPEN_QUICK,
  'open-chat': ATHENA_INTENT_TYPES.OPEN_CHAT,
  'open-project': ATHENA_INTENT_TYPES.OPEN_PROJECT,
  'open-calendar': ATHENA_INTENT_TYPES.OPEN_CALENDAR,
  'open-task': ATHENA_INTENT_TYPES.OPEN_TASK,
  'open-workspace': ATHENA_INTENT_TYPES.OPEN_WORKSPACE,
  'start-voice': ATHENA_INTENT_TYPES.START_VOICE,
  'new-task': ATHENA_INTENT_TYPES.NEW_TASK,
  'new-reminder': ATHENA_INTENT_TYPES.NEW_REMINDER,
  'capture-idea': ATHENA_INTENT_TYPES.CAPTURE_IDEA,
  'import-content': ATHENA_INTENT_TYPES.IMPORT_CONTENT,
});

const SOURCE_VALUES = new Set(['deep_link', 'native_intent', 'shortcut', 'widget', 'tile']);
const IntentContext = createContext(null);
const SOURCE_ALIASES = Object.freeze({
  quick_settings_tile: 'tile',
  static_shortcut: 'shortcut',
  shortcut: 'shortcut',
  privacy_safe_widget: 'widget',
  widget: 'widget',
  deep_link: 'deep_link',
});
const NATIVE_INTENT_ALIASES = Object.freeze({
  open_home: ATHENA_INTENT_TYPES.OPEN_HOME,
  open_quick: ATHENA_INTENT_TYPES.OPEN_QUICK,
  open_voice: ATHENA_INTENT_TYPES.START_VOICE,
  new_task: ATHENA_INTENT_TYPES.NEW_TASK,
  new_reminder: ATHENA_INTENT_TYPES.NEW_REMINDER,
  capture_idea: ATHENA_INTENT_TYPES.CAPTURE_IDEA,
  open_chat: ATHENA_INTENT_TYPES.OPEN_CHAT,
  open_project: ATHENA_INTENT_TYPES.OPEN_PROJECT,
  open_calendar: ATHENA_INTENT_TYPES.OPEN_CALENDAR,
  open_task: ATHENA_INTENT_TYPES.OPEN_TASK,
  open_workspace: ATHENA_INTENT_TYPES.OPEN_WORKSPACE,
  import_content: ATHENA_INTENT_TYPES.IMPORT_CONTENT,
});

function safeOwnerId(value) {
  const ownerId = typeof value === 'string' ? value.trim() : String(value || '').trim();
  return SAFE_OWNER.test(ownerId) ? ownerId : '';
}

function randomId() {
  try { return `intent-${globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`; }
  catch { return `intent-${Math.random().toString(36).slice(2)}`; }
}

function safeSource(value) {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  const aliased = SOURCE_ALIASES[normalized] || normalized;
  return SOURCE_VALUES.has(aliased) ? aliased : 'native_intent';
}

function safeCreatedAt(value) {
  const numeric = Number(value);
  const timestamp = Number.isFinite(numeric) && numeric > 0
    ? numeric
    : Date.parse(value || '');
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : new Date().toISOString();
}

function sourceEvent(source) {
  if (source === 'shortcut') return { type: ATHENA_EVENT_TYPES.SHORTCUT_USED, source: ATHENA_EVENT_SOURCES.SHORTCUT };
  if (source === 'widget') return { type: ATHENA_EVENT_TYPES.WIDGET_ACTION, source: ATHENA_EVENT_SOURCES.WIDGET };
  if (source === 'tile') return { type: ATHENA_EVENT_TYPES.TILE_ACTION, source: ATHENA_EVENT_SOURCES.TILE };
  return { type: ATHENA_EVENT_TYPES.NATIVE_INTENT_RECEIVED, source: ATHENA_EVENT_SOURCES.INTENT };
}

function normalizeIntent(raw, fallbackSource = 'native_intent') {
  const input = raw && typeof raw === 'object' ? raw : {};
  const rawType = typeof (input.type || input.intent || input.action) === 'string'
    ? String(input.type || input.intent || input.action).trim()
    : '';
  const type = NATIVE_INTENT_ALIASES[rawType.toLowerCase()] || rawType.toUpperCase();
  if (!INTENT_TYPES.has(type)) return null;
  const source = safeSource(input.source || fallbackSource);
  // Android persists `eventId` across a cold-start drain and its live
  // listener. Preserve it as the canonical idempotency key so the same native
  // action cannot surface twice when both paths race.
  const suppliedId = input.id || input.eventId || input.event_id;
  const id = typeof suppliedId === 'string' && /^[A-Za-z0-9._:-]{1,180}$/.test(suppliedId)
    ? suppliedId
    : randomId();
  const createdAt = safeCreatedAt(input.createdAt || input.created_at || input.timestamp);
  const createdAtMs = Date.parse(createdAt);
  if (!Number.isFinite(createdAtMs) || Math.abs(Date.now() - createdAtMs) > MAX_INTENT_AGE_MS) return null;
  return Object.freeze({
    id,
    type,
    source,
    createdAt,
    // `ownerId: null` denotes the one-time, pre-login safe intent. No native
    // body or account data is persisted in this object.
    ownerId: safeOwnerId(input.ownerId || input.owner_id) || null,
  });
}

function readPendingIntents() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = JSON.parse(window.sessionStorage.getItem(INTENT_STORAGE_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item) => normalizeIntent(item, item?.source))
      .filter(Boolean)
      .slice(-MAX_PENDING_INTENTS);
  } catch {
    return [];
  }
}

function writePendingIntents(intents) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(INTENT_STORAGE_KEY, JSON.stringify(intents.slice(-MAX_PENDING_INTENTS)));
  } catch {
    // A private WebView can disallow storage. The in-memory DOM event still
    // gives an already-open app a chance to display the safe UI request.
  }
}

function notifyIntent(intent) {
  if (typeof window === 'undefined' || !intent) return;
  try { window.dispatchEvent(new CustomEvent(INTENT_EVENT_NAME, { detail: intent })); } catch { /* noop */ }
}

/**
 * Parses only `athena://action/<approved-slug>`. Query data and additional
 * segments are deliberately ignored, so a link can never smuggle an action
 * payload into the app.
 */
export function parseAthenaActionDeepLink(rawUrl) {
  if (typeof rawUrl !== 'string' || !rawUrl.trim()) return null;
  try {
    const parsed = new URL(rawUrl);
    if (parsed.protocol !== 'athena:' || parsed.hostname.toLowerCase() !== 'action') return null;
    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments.length !== 1) return null;
    const type = ACTION_SLUGS[decodeURIComponent(segments[0]).toLowerCase()];
    return type ? { type, source: 'deep_link' } : null;
  } catch {
    return null;
  }
}

/** Enqueues a safe one-time action before login or while the Home is hidden. */
export function queueAthenaIntent(rawIntent, { source = 'native_intent', ownerId = '' } = {}) {
  const fromUrl = typeof rawIntent === 'string' ? parseAthenaActionDeepLink(rawIntent) : null;
  const intent = normalizeIntent({
    ...(fromUrl || (rawIntent && typeof rawIntent === 'object' ? rawIntent : {})),
    source: fromUrl?.source || source,
    ownerId,
  }, source);
  if (!intent) return null;

  const existing = readPendingIntents();
  if (existing.some((item) => item.id === intent.id || (item.type === intent.type && item.source === intent.source && item.ownerId === intent.ownerId))) {
    return intent;
  }
  writePendingIntents([...existing, intent]);
  const event = sourceEvent(intent.source);
  emitAthenaEvent({
    ...event,
    metadata: { intent: intent.type },
    idempotencyKey: `intent:${intent.id}`,
  });
  notifyIntent(intent);
  return intent;
}

function removeStoredIntent(intentId) {
  const remaining = readPendingIntents().filter((item) => item.id !== intentId);
  writePendingIntents(remaining);
}

async function nativeCall(methodNames, args = undefined) {
  for (const method of methodNames) {
    try {
      if (typeof NativeIntents?.[method] !== 'function') continue;
      return args === undefined ? await NativeIntents[method]() : await NativeIntents[method](args);
    } catch {
      // An older APK simply has no Phase 5 native intent surface.
    }
  }
  return null;
}

async function addNativeIntentListener(listener) {
  try {
    const handle = await NativeIntents.addListener('intentEvent', listener);
    return () => { try { void handle?.remove?.(); } catch { /* noop */ } };
  } catch {
    return () => {};
  }
}

/** Auth cleanup boundary; does not change Android app permissions. */
export async function clearAthenaIntentRuntime(ownerId = '') {
  if (typeof window !== 'undefined') {
    try { window.sessionStorage.removeItem(INTENT_STORAGE_KEY); } catch { /* noop */ }
  }
  const safeOwner = safeOwnerId(ownerId);
  if (safeOwner) {
    // Automatic logout with an older APK is a safe no-op. It must never use
    // the legacy global clear after another owner could have been bound.
    return nativeCall(['clearForOwner'], { ownerId: safeOwner });
  }
  return nativeCall(['clearForUser', 'clearIdentity']);
}

export function AthenaIntentProvider({ children }) {
  const { user } = useAuth();
  const ownerId = safeOwnerId(user?.id);
  const [pendingIntent, setPendingIntent] = useState(null);
  const ownerRef = useRef(ownerId);
  const handledRef = useRef(new Set());

  useEffect(() => { ownerRef.current = ownerId; }, [ownerId]);

  const surfaceIntent = useCallback((raw, source = 'native_intent') => {
    const queued = queueAthenaIntent(raw, { source, ownerId: ownerRef.current });
    if (!queued) return null;
    if (!ownerRef.current) return queued;
    if (queued.ownerId && queued.ownerId !== ownerRef.current) return null;
    if (handledRef.current.has(queued.id)) return queued;
    handledRef.current.add(queued.id);
    setPendingIntent(queued);
    return queued;
  }, []);

  useEffect(() => {
    let active = true;
    let removeNativeListener = () => {};
    const onWindowIntent = (event) => {
      if (!active || !ownerRef.current) return;
      const candidate = normalizeIntent(event?.detail, event?.detail?.source);
      if (!candidate || (candidate.ownerId && candidate.ownerId !== ownerRef.current)) return;
      if (handledRef.current.has(candidate.id)) return;
      handledRef.current.add(candidate.id);
      setPendingIntent(candidate);
    };
    if (typeof window !== 'undefined') window.addEventListener(INTENT_EVENT_NAME, onWindowIntent);

    const bootstrap = async () => {
      if (!ownerId) {
        handledRef.current.clear();
        setPendingIntent(null);
        return;
      }
      const binding = await nativeCall(['bindOwner'], { ownerId });
      if (!active) return;
      if (binding?.ownerChanged === true) {
        // Native bindOwner already cleared the previous owner's storage *and*
        // bound this new owner. Do not call clearForUser here or we would
        // immediately erase the just-established account association.
        handledRef.current.clear();
        setPendingIntent(null);
      }
      const nativePending = await nativeCall(['drain', 'getPending', 'getPendingEvents']);
      const items = Array.isArray(nativePending?.intents)
        ? nativePending.intents
        : Array.isArray(nativePending?.events)
          ? nativePending.events
        : Array.isArray(nativePending) ? nativePending : [];
      items.forEach((item) => surfaceIntent(item, item?.source || 'native_intent'));

      const stored = readPendingIntents();
      const available = stored.find((item) => !item.ownerId || item.ownerId === ownerId);
      if (available && !handledRef.current.has(available.id)) {
        handledRef.current.add(available.id);
        setPendingIntent(available);
      }
    };

    void bootstrap();
    void addNativeIntentListener((raw) => {
      if (active) surfaceIntent(raw, raw?.source || 'native_intent');
    }).then((remove) => {
      if (active) removeNativeListener = remove;
      else remove();
    });

    return () => {
      active = false;
      if (typeof window !== 'undefined') window.removeEventListener(INTENT_EVENT_NAME, onWindowIntent);
      removeNativeListener();
    };
  }, [ownerId, surfaceIntent]);

  const consumeIntent = useCallback((intentId = pendingIntent?.id) => {
    if (!intentId) return null;
    const current = pendingIntent?.id === intentId ? pendingIntent : null;
    removeStoredIntent(intentId);
    setPendingIntent((previous) => previous?.id === intentId ? null : previous);
    return current;
  }, [pendingIntent]);

  const dismissIntent = useCallback((intentId = pendingIntent?.id) => consumeIntent(intentId), [consumeIntent, pendingIntent]);

  const value = useMemo(() => ({
    pendingIntent,
    consumeIntent,
    dismissIntent,
    queueIntent: surfaceIntent,
    supportedActions: Object.freeze(Object.values(ATHENA_INTENT_TYPES)),
  }), [consumeIntent, dismissIntent, pendingIntent, surfaceIntent]);

  return <IntentContext.Provider value={value}>{children}</IntentContext.Provider>;
}

export function useAthenaIntent() {
  const context = useContext(IntentContext);
  if (!context) throw new Error('useAthenaIntent must be used inside AthenaIntentProvider');
  return context;
}
